import {
  FC,
  useState,
  useEffect,
  useMemo,
  useCallback,
  createContext,
  useContext,
  useRef,
  ReactNode
} from 'react'
import { LayoutSplashScreen } from '../../../../_metronic/layout/core'
import { AuthModel } from './_models'
import * as authHelper from './AuthHelpers'
import { useDispatch } from 'react-redux'
import { setSession } from '../../../store/slice/auth-slice'
import { resetAiState } from '../../../store/slice/ai-slice'
import { setIntegrations } from '../../../store/slice/integration-slice'

type AuthContextProps = any

// type AuthContextProps = {
//   auth: AuthModel | undefined
//   saveAuth: (auth: AuthModel | undefined) => void
//   currentUser: UserModel | undefined
//   setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
//   logout: () => void
// }

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {
    // do nothing 
  },
  currentUser: undefined,
  setCurrentUser: () => {
    // do nothing 
  },
  logout: () => {
    // do nothing 
  },
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = (): any => {
  return useContext(AuthContext)
}


const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const dispatch = useDispatch()
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())

  const [currentUser, setCurrentUser] = useState<any>()
  // useCallback so the identity is stable across AuthProvider re-renders.
  // Previously these (and the context value object) were recreated every
  // render, so any setCurrentUser/saveAuth re-rendered every useAuth()
  // consumer with fresh identities — amplifying an incidental re-render
  // into a "Maximum update depth exceeded" cascade through Transitions.
  const saveAuth = useCallback((auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }, [])

  const logout = useCallback(() => {
    // Use consistent storage cleanup through AuthHelpers
    saveAuth(undefined) // This already calls authHelper.removeAuth() which handles secure cleanup
    setCurrentUser(undefined)
    setTimeout(() => {
      // Additional cleanup for other stored data
      localStorage.removeItem('planData')
      localStorage.removeItem('widget_user_auth_token')
      sessionStorage.removeItem('isTokenExpire');
      dispatch(setSession(false))
      dispatch(setIntegrations([]))
      dispatch(resetAiState()) // Clear AI chat and messages from Redux
    }, 1000);
  }, [saveAuth, dispatch])

  const contextValue = useMemo(
    () => ({ auth, saveAuth, currentUser, setCurrentUser, logout }),
    [auth, currentUser, saveAuth, logout]
  )

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<{ children: ReactNode }> = ({ children }) => {
  const { auth, logout, setCurrentUser } = useAuth()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  // We should request user by authToken (IN OUR EXAMPLE IT'S API_TOKEN) before rendering the application
  useEffect(() => {
    const requestUser = () => {
      try {
        if (!didRequest.current) {
          // Use consistent storage method from AuthHelpers instead of direct localStorage access
          const authData = authHelper.getAuth()
          if (authData) {
            setCurrentUser(authData)
          }
        }
      } catch (error) {
        if (!didRequest.current) {
          logout()
        }
      } finally {
        setShowSplashScreen(false)
      }

      return () => (didRequest.current = true)
    }

    if (auth) {
      requestUser()
    } else {
      logout()
      setShowSplashScreen(false)
    }
    // eslint-disable-next-line
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export { AuthProvider, AuthInit, useAuth }
